import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { ReactNode } from 'react';

export type Period = 'today' | 'day' | 'week' | 'month' | 'quarter';

export interface SubTab {
  id: string;
  label: string;
}

export interface PeriodNavigationProps {
  /** Formatted label for the current period (e.g. "April 2026") */
  label: string;
  onPrev: () => void;
  onNext: () => void;
  /** Show a reset button (e.g. "This Month") when not on current period */
  resetLabel?: string;
  onReset?: () => void;
  /** Accessible label for prev button (i18n) */
  prevAriaLabel?: string;
  /** Accessible label for next button (i18n) */
  nextAriaLabel?: string;
  /**
   * The bar shows the exact window a link named (a dashboard card's days), not
   * the period: no period tab is selected until the viewer picks one.
   */
  exactWindow?: boolean;
}

export interface PeriodBarProps {
  periods?: { id: Period; label: string }[];
  activePeriod: Period;
  onPeriodChange: (period: Period) => void;
  /** Optional date navigation (prev/next arrows + reset) */
  navigation?: PeriodNavigationProps;
  subTabs?: SubTab[];
  activeSubTab?: string;
  onSubTabChange?: (tab: string) => void;
  /** Extra controls (filters, toggles) rendered at the right end */
  actions?: ReactNode;
  /** Show an "updating" indicator while widget data refetches after a
   *  period/date switch — otherwise the switch looks like a no-op. */
  isLoading?: boolean;
  className?: string;
}

export function PeriodBar({
  periods,
  activePeriod,
  onPeriodChange,
  navigation,
  subTabs,
  activeSubTab,
  onSubTabChange,
  actions,
  isLoading,
  className,
}: PeriodBarProps) {
  const { t } = useTranslation('common');
  // Localized default tabs when the caller doesn't supply its own `periods`.
  const resolvedPeriods = periods ?? [
    { id: 'today' as Period, label: t('common.period.today', 'Today') },
    { id: 'week' as Period, label: t('common.period.week', 'Week') },
    { id: 'month' as Period, label: t('common.period.month', 'Month') },
    { id: 'quarter' as Period, label: t('common.period.quarter', 'Quarter') },
  ];
  return (
    <div
      data-testid="period-bar"
      className={cn('mb-4 bg-bg-surface border border-border rounded-md', className)}
    >
      {/* Две строки, а не одна: у клиента вкладок стало девять, и в один ряд
          с периодами, стрелками и действиями они уже не помещались — крайние
          уезжали за границу панели (#891). Периоды и действия остаются здесь,
          вкладки — отдельной строкой ниже. Строка переносится: на узкой панели
          действия уходят второй строкой целиком, а не за её край (#2315). */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-[3px]">
        <div className="flex items-center gap-3">
          <div data-testid="period-tabs" className="flex gap-1">
            {resolvedPeriods.map((period) => (
              <Button
                key={period.id}
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onPeriodChange(period.id)}
                className={cn(
                  'rounded-sm px-3 py-1 text-xs font-medium transition-all duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  activePeriod === period.id && !navigation?.exactWindow
                    ? 'bg-primary-dim text-primary-fg font-medium'
                    : 'text-fg-muted hover:text-fg-secondary'
                )}
              >
                {period.label}
              </Button>
            ))}
          </div>

          {navigation && (
            <>
              <div className="w-px h-4 bg-border" />
              <div data-testid="period-navigation" className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={navigation.onPrev}
                  aria-label={navigation.prevAriaLabel ?? 'Previous period'}
                  className="rounded-sm px-1.5 py-1 text-fg-muted hover:text-fg transition-colors duration-150"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="min-w-[140px] text-center text-xs font-medium text-fg tabular-nums px-1">
                  {navigation.label}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={navigation.onNext}
                  aria-label={navigation.nextAriaLabel ?? 'Next period'}
                  className="rounded-sm px-1.5 py-1 text-fg-muted hover:text-fg transition-colors duration-150"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                {navigation.resetLabel && navigation.onReset && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={navigation.onReset}
                    className="rounded-sm px-3 py-1 text-xs font-medium text-primary hover:text-primary transition-colors duration-150"
                  >
                    {navigation.resetLabel}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>

        <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
          {/* The indicator keeps its place while hidden: in a row that wraps,
              appearing and vanishing would move the actions between lines on
              every refetch. */}
          <div
            data-testid={isLoading ? 'period-bar-loading' : undefined}
            className={cn(
              'flex items-center gap-1.5 px-2 text-xs font-medium text-primary',
              !isLoading && 'invisible'
            )}
            role="status"
            aria-live="polite"
            aria-hidden={!isLoading}
          >
            <Loader2
              className={cn('h-3.5 w-3.5', isLoading && 'animate-spin')}
              aria-hidden="true"
            />
            {t('common.updatingData', 'Updating…')}
          </div>

          {actions}
        </div>
      </div>

      {subTabs && subTabs.length > 0 && (
        <>
          <div className="h-px bg-border" />
          {/* flex-wrap — чтобы на узком экране вкладки переносились, а не
              уезжали за край и не прятались за невидимой прокруткой. */}
          <div
            data-testid="period-sub-tabs"
            className="flex flex-wrap items-center gap-3 p-[3px]"
          >
            {subTabs.map((tab) => (
              <Button
                key={tab.id}
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onSubTabChange?.(tab.id)}
                className={cn(
                  'text-xs transition-all duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  activeSubTab === tab.id
                    ? 'text-primary font-medium'
                    : 'text-fg-muted hover:text-fg'
                )}
              >
                {tab.label}
              </Button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
